import { syncChildId } from '../utils/syncChildId';

/**
 * Onboarding Flow Constants
 *
 * Defines the sequential 3-step flow for first-time users:
 * Step 1: Child Details
 * Step 2: Language & Communication Assessment
 * Step 3: Personal, Social & Emotional Assessment
 */

export const ONBOARDING_STEPS = {
  CHILD_DETAILS: 1,
  LANGUAGE_ASSESSMENT: 2,
  PERSONAL_SOCIAL_ASSESSMENT: 3,
} as const;

export const TOTAL_STEPS = 3;

/**
 * V1 assessment categories in sequential order.
 * Used for onboarding flow navigation and profile display.
 */
export const V1_CATEGORIES = [
  {
    key: 'language_and_communication',
    name: 'Language and communication',
    step: ONBOARDING_STEPS.LANGUAGE_ASSESSMENT,
  },
  {
    key: 'personal_social_and_emotional',
    name: 'Personal, social and emotional',
    step: ONBOARDING_STEPS.PERSONAL_SOCIAL_ASSESSMENT,
  },
] as const;

/**
 * Query parameter names for flow state
 */
export const FLOW_PARAMS = {
  FLOW_TYPE: 'flow',
  STEP_NUMBER: 'step',
  CATEGORY: 'category',
} as const;

export const FLOW_TYPES = {
  ONBOARDING: 'onboarding',
} as const;

/**
 * LocalStorage key for first-time onboarding flag
 */
export const STORAGE_KEYS = {
  IS_FIRST_TIME_ONBOARDING: 'isFirstTimeOnboarding',
} as const;

/**
 * Get the next category in the assessment sequence.
 * Returns null if currentCategory is the last one.
 */
export function getNextCategory(currentCategoryKey: string): typeof V1_CATEGORIES[number] | null {
  const currentIndex = V1_CATEGORIES.findIndex(cat => cat.key === currentCategoryKey);
  if (currentIndex === -1 || currentIndex === V1_CATEGORIES.length - 1) {
    return null;
  }
  return V1_CATEGORIES[currentIndex + 1];
}

/**
 * Get category by key
 */
export function getCategoryByKey(key: string): typeof V1_CATEGORIES[number] | undefined {
  return V1_CATEGORIES.find(cat => cat.key === key);
}

/**
 * Check if onboarding flow is active (localStorage flag)
 */
export function isOnboardingActive(): boolean {
  return localStorage.getItem(STORAGE_KEYS.IS_FIRST_TIME_ONBOARDING) === 'true';
}

/**
 * Set onboarding flag to active
 */
export function startOnboarding(): void {
  localStorage.setItem(STORAGE_KEYS.IS_FIRST_TIME_ONBOARDING, 'true');
}

/**
 * Clear onboarding flag (called after completing final step)
 */
export function completeOnboarding(): void {
  localStorage.removeItem(STORAGE_KEYS.IS_FIRST_TIME_ONBOARDING);
}

/**
 * Initialize onboarding state from backend.
 * Called on app load after authentication to sync localStorage with backend state.
 *
 * Cross-device scenario:
 * - User completes onboarding on Device A
 * - User logs in on Device B (no localStorage flag)
 * - This function checks backend: if children exist, skip onboarding
 */
export async function initializeOnboardingStateFromBackend(): Promise<void> {
  try {
    // Dynamic import to avoid circular dependencies
    const { api } = await import('../services/api');

    const children = await api.getChildren();

    syncChildId(children);

    if (children.length === 0) {
      // User has NO children → first time user
      startOnboarding();
    } else {
      // User has children → already completed onboarding (or added child via other device)
      // Ensure flag is cleared so step indicator doesn't show
      completeOnboarding();
    }
  } catch (error) {
    console.error('[onboardingFlow] Failed to check children from backend:', error);
    // Don't set flag on error - let user navigate manually
    throw error;
  }
}
